import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DzGridConstants } from '@blk/daisy/util';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import * as cloneDeep from 'lodash/cloneDeep';

import { FilterSelectionState, highlightedToDropdownFiltersMapping, ModelsExperienceDropdownFilters, ModelsExperienceFilters } from '../models';

@Component({
	selector: 'mb-dropdown-filters',
	templateUrl: './dropdown-filters.component.html',
	styleUrls: ['./dropdown-filters.component.scss']
})
export class DropdownFiltersComponent implements OnChanges {

	@Input() public filtersList: ModelsExperienceFilters[] = [];
	@Input() public urlFilters: ModelsExperienceFilters[] = [];
	@Input() public selectedHighlightedFilterConfig: FilterSelectionState[];
	@Output() public selectedDropdownFilters: EventEmitter<ModelsExperienceFilters[]> = new EventEmitter<ModelsExperienceFilters[]>();
	@InjectConfig(ModelsExperienceConfigKey.SHOW_DROPDOWN_FILTERS_MORE_LESS_BUTTON) public readonly showDropDownFiltersMoreLessBtn: boolean = true;

	public dropdownFilterList: ModelsExperienceDropdownFilters[] = [];
	public displayFiltersList: ModelsExperienceDropdownFilters[];
	public selectedFiltersList: ModelsExperienceFilters[] = [];
	public showMore: boolean = false;

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.filtersList) {
			this.dropdownFilterList = this.filtersList.map((filter) => {
				const filterObj: ModelsExperienceDropdownFilters = { ...filter, options: [] };
				filter.options.forEach((option) => {
					const optionObj = { optionId: option, selected: false };
					filterObj.options.push(optionObj);
				});
				return filterObj;
			});
		}
		this.fetchDisplayFilters();
		if (changes.urlFilters) {
			this.setPreSelectedFiltersFromUrl();
		}

		if (!!changes.selectedHighlightedFilterConfig?.currentValue) {
			this.updateFilterListOnHighLightedFilterChange();
		}
	}

	public getWidthClass(filter: ModelsExperienceFilters): string {
		if (filter.meFilterId === 'riskProfile' || filter.meFilterId === 'modelCategoryType') {
			return 'dz-col-xl-5 dz-col-lg-6';
		} else {
			return 'dz-col-xl-4 dz-col-lg-6';
		}
	}

	public getSelectedFiltersList(selectedFilter: ModelsExperienceFilters): void {
		const index = this.selectedFiltersList.findIndex(filter => filter.meFilterId === selectedFilter.meFilterId);
		if (selectedFilter.options.length) {
			if (index > -1) {
				this.selectedFiltersList[index] = selectedFilter;
			} else {
				this.selectedFiltersList.push(selectedFilter);
			}
		} else {
			if (index > -1) {
				this.selectedFiltersList.splice(index, 1);
			}
		}

		this.selectedDropdownFilters.emit(this.selectedFiltersList);
	}
	// this method updates selectedFilter for each highlighted Filter config and emits the final change 
	public updateFilterListOnHighLightedFilterChange(): void {
		this.selectedHighlightedFilterConfig?.forEach(filterConfig => {
			this.updateSelectedFilterList(filterConfig);
		});

		this.selectedDropdownFilters.emit(this.selectedFiltersList);
		this.fetchDisplayFilters();
	}

	// this method updates the selectedFilter option list based highlighted filter selection 
	private updateSelectedFilterList(selectedFilterConfig: FilterSelectionState): void {
		const selectedDropdownFiltersList = cloneDeep(this.selectedFiltersList);
		// fetch corresponding mapping of dropdown filter config (id and options to be selected) per selectedHighlighted filter
		const dropdownFilterConfig = highlightedToDropdownFiltersMapping[selectedFilterConfig.id];
		if (dropdownFilterConfig) {
			const filterIndex = selectedDropdownFiltersList.findIndex(filter => filter.meFilterId === dropdownFilterConfig.dropdownFilterId);
			if (selectedFilterConfig.checked) {
				if (filterIndex > -1) {
					dropdownFilterConfig.options?.forEach(option => {
						const optionIndex = selectedDropdownFiltersList[filterIndex].options.indexOf(option);
						if (optionIndex === -1) {
							selectedDropdownFiltersList[filterIndex].options.push(option);
						}
					});
				} else {
					const filterObj: ModelsExperienceFilters = {
						...this.dropdownFilterList.find(filter => filter.meFilterId === dropdownFilterConfig.dropdownFilterId),
						options: dropdownFilterConfig.options
					};
					selectedDropdownFiltersList.push(filterObj);
				}
			} else {
				if (filterIndex > -1) {
					dropdownFilterConfig.options?.forEach(option => {
						const optionIndex = selectedDropdownFiltersList[filterIndex].options.indexOf(option);
						if (optionIndex > -1) {
							selectedDropdownFiltersList[filterIndex].options.splice(optionIndex, 1);

						}
					});

					if (!selectedDropdownFiltersList[filterIndex].options.length) {
						selectedDropdownFiltersList.splice(filterIndex, 1);
					}
				}
			}

			this.updateDropDownFilterList(dropdownFilterConfig, selectedFilterConfig);
			this.selectedFiltersList = selectedDropdownFiltersList;
		}
	}

	// this method updates dropdown filter list options (selected flag) based on highlighted filter select deselect
	private updateDropDownFilterList(dropdownFilterConfig, selectedFilterConfig): void {
		const filterIndex = this.dropdownFilterList.findIndex(filter => filter.meFilterId === dropdownFilterConfig.dropdownFilterId);
		if (filterIndex !== -1) {
			const isFilterChecked = selectedFilterConfig.checked;
			const updatedOptions = this.dropdownFilterList[filterIndex].options.map(option => {
				if (dropdownFilterConfig.options.includes(option.optionId)) {
					option.selected = isFilterChecked;
				}
				return option;
			});

			this.dropdownFilterList[filterIndex] = {
				...this.dropdownFilterList[filterIndex],
				options: updatedOptions
			};
		}
	}

	public toggleView(): void {
		this.showMore = !this.showMore;
		this.fetchDisplayFilters();
	}

	private fetchDisplayFilters(): void {
		if (window.innerWidth < DzGridConstants.BREAKPOINTS.lg) {
			this.displayFiltersList = this.showMore ? [] : this.dropdownFilterList;
		} else {
			this.displayFiltersList = this.showMore ? this.dropdownFilterList.filter((filter) => filter?.meFilterGroup === 'Standard') : this.dropdownFilterList;
		}
	}

	/**
	 * This function is used to set 'selected' value for each option in dropdown filter array from the list of url filters
	 * 
	 */
	private setPreSelectedFiltersFromUrl(): void {
		this.selectedFiltersList = cloneDeep(this.urlFilters);
		this.dropdownFilterList = this.dropdownFilterList.map((filter) => {
			//checking if the filter is present in dropdown filters list using meFilterId
			if (this.urlFilters.some((urlFilter) => urlFilter.meFilterId === filter.meFilterId)) {
				const existingFilter = this.urlFilters.find((filterObj) => filterObj.meFilterId === filter.meFilterId);
				//if the filter exist for that filter id, then we push selected value as true or false depending on the options present in url filters
				filter.options = filter.options.map((option) => {
					option.selected = existingFilter.options.some((selectedOption) => selectedOption === option.optionId);
					return option;
				});
			} else if (!this.urlFilters.length) {
				// All of the filters have been reset
				filter.options = filter.options.map((option) => {
					option.selected = false;
					return option;
				});
			}
			return { ...filter };
		});
		this.selectedDropdownFilters.emit(this.selectedFiltersList);
	}
}
